import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Avatar from 'flarum/common/components/Avatar';
import listItems from 'flarum/common/helpers/listItems';
import { trans, truncate, displayName, userRoute, navigate, formatTimeLabel, formatDate, coverPosition } from '../../utils';
import CakedayBadge from './CakedayBadge';
import trustedHtml from '../../../common/trustedHtml';

/** Conta numérica do atributo, ou null quando ausente (renderiza "–"). */
function countOrNull(value) {
  return typeof value === 'number' && !isNaN(value) ? value : null;
}
const OPEN_DELAY = 350;
const CLOSE_DELAY = 150;
const CARD_WIDTH = 330;
const CARD_EST_HEIGHT = 330;
/** O mais perto da borda que a seta chega: o raio do canto do card + meia seta. */
const ARROW_INSET = 26;
/** Num gatilho largo (o nome) a seta mira o começo dele, não o meio. */
const ARROW_REACH = 28;

/**
 * Estado do card em nível de MÓDULO, renderizado num portal próprio no <body>
 * via m.render — nunca dentro da árvore do componente trigger. Motivo: o
 * PostStream recria instâncias do componente mantendo o mesmo nó DOM, e os
 * event handlers antigos ("zumbis") continuam ligados ao elemento; se o estado
 * morasse na instância, o open rodava numa instância descartada cujo view
 * nunca mais renderiza. Com o estado e o render fora do vdom, qualquer
 * instância (viva ou zumbi) dispara o mesmo singleton e o card sempre monta.
 */
let portal = null;
let currentUser = null;
let triggerEl = null;
let cardEl = null;
let openTimer = null;
let closeTimer = null;
let pendingUser = null;
let pendingEl = null;
let pos = {
  top: 0,
  left: 0,
  above: false,
  arrow: ARROW_INSET
};

/** Perfis já hidratados nesta sessão (dedup do fetch; libera no erro p/ retry). */
const hydratedIds = new Set();
export function userCardEnabled() {
  return !!app.forum?.attribute?.('avocadoUserCardEnabled');
}
function ensurePortal() {
  if (!portal || !document.body.contains(portal)) {
    portal = document.createElement('div');
    portal.className = 'AvocadoUserCard-portalRoot';
    document.body.appendChild(portal);
  }
  return portal;
}
function renderPortal() {
  m.render(ensurePortal(), currentUser ? cardVnode(currentUser) : null);
}
const onMouseMove = e => {
  if (!currentUser) return;
  const target = e.target;
  if (target && (cardEl?.contains(target) || triggerEl?.contains(target))) {
    cancelClose();
    return;
  }
  if (!closeTimer) scheduleClose();
};
const onDocDown = e => {
  const target = e.target;
  if (target && (cardEl?.contains(target) || triggerEl?.contains(target))) return;
  closeCard();
};
const onKeyDown = e => {
  if (e.key === 'Escape') closeCard();
};
const onDismiss = () => closeCard();
function watch() {
  document.addEventListener('mousemove', onMouseMove, {
    passive: true
  });
  document.addEventListener('mousedown', onDocDown, true);
  document.addEventListener('keydown', onKeyDown);
  document.documentElement.addEventListener('mouseleave', onDismiss);
  window.addEventListener('scroll', onDismiss, {
    passive: true
  });
  window.addEventListener('blur', onDismiss);
}
function unwatch() {
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('mousedown', onDocDown, true);
  document.removeEventListener('keydown', onKeyDown);
  document.documentElement.removeEventListener('mouseleave', onDismiss);
  window.removeEventListener('scroll', onDismiss);
  window.removeEventListener('blur', onDismiss);
}
function clearTimers() {
  if (openTimer) window.clearTimeout(openTimer);
  if (closeTimer) window.clearTimeout(closeTimer);
  openTimer = closeTimer = null;
}
export function scheduleOpen(user, el) {
  if (!user || !el || !userCardEnabled()) return;
  cancelClose();
  if (currentUser && triggerEl === el) return;
  pendingUser = user;
  pendingEl = el;
  if (openTimer) return;
  openTimer = window.setTimeout(() => {
    openTimer = null;
    openCard(pendingUser, pendingEl);
  }, OPEN_DELAY);
}
export function scheduleClose() {
  if (openTimer) {
    window.clearTimeout(openTimer);
    openTimer = null;
  }
  if (!currentUser || closeTimer) return;
  closeTimer = window.setTimeout(() => {
    closeTimer = null;
    closeCard();
  }, CLOSE_DELAY);
}
function cancelClose() {
  if (closeTimer) {
    window.clearTimeout(closeTimer);
    closeTimer = null;
  }
}
function openCard(user, el) {
  if (!user || !el || !el.isConnected) return;
  const rect = el.getBoundingClientRect();
  const pad = 8;
  pos = {
    left: Math.max(pad, Math.min(rect.left, window.innerWidth - CARD_WIDTH - pad)),
    above: rect.bottom + CARD_EST_HEIGHT > window.innerHeight && rect.top > CARD_EST_HEIGHT,
    top: 0,
    arrow: 0
  };
  pos.top = pos.above ? rect.top : rect.bottom;
  // A seta aponta para o gatilho (avatar ou nome), mesmo quando o card foi
  // empurrado para caber na tela — só não entra na curva do canto.
  const target = rect.left + Math.min(rect.width / 2, ARROW_REACH);
  pos.arrow = Math.max(ARROW_INSET, Math.min(target - pos.left, CARD_WIDTH - ARROW_INSET));
  currentUser = user;
  triggerEl = el;
  watch();
  hydrate(user);
  renderPortal();
}
export function closeCard() {
  clearTimers();
  unwatch();
  if (!currentUser) return;
  currentUser = null;
  triggerEl = null;
  cardEl = null;
  renderPortal();
}
function hydrate(user) {
  const id = String(user?.id?.() || '');
  if (!id || hydratedIds.has(id)) return;
  hydratedIds.add(id);
  app.store.find('users', id).then(() => renderPortal()).catch(() => {
    hydratedIds.delete(id);
  });
}
function cardVnode(user) {
  const profileHref = userRoute(user);
  const cover = user.attribute?.('cover_thumbnail') || user.attribute?.('cover');
  // fof/user-bio renderiza a bio no servidor (s9e) e expõe o HTML em `bioHtml`;
  // renderamos isso — senão o BBCode/markdown apareceria cru ([b], [url], …).
  // `bio` (texto puro) é o fallback quando a formatação/extensão está ausente.
  const bioHtml = user.bioHtml?.();
  const bio = user.bio?.() || user.attribute?.('bio') || '';
  const joined = user.joinTime?.();
  const lastSeen = user.lastSeenAt?.();
  const online = !!lastSeen && Date.now() - new Date(lastSeen).getTime() < 5 * 60 * 1000;
  const discussions = countOrNull(user.attribute?.('discussionCount'));
  const posts = countOrNull(user.attribute?.('commentCount'));
  const badges = user.badges?.()?.toArray?.() || [];
  const self = app.session.user;
  const canMessage = !!self && String(self.id?.()) !== String(user.id?.()) && typeof self.canSendAnyMessage === 'function' && self.canSendAnyMessage();

  // Selo da ext ramon-verified, quando instalada — mesmo componente das outras
  // superfícies, resolvido pelo registry (ausente = null).
  const VerifiedBadge = flarum.reg?.get?.('ramon-verified', 'common/components/VerifiedBadge');
  const verified = VerifiedBadge && user.attribute?.('isVerified') ? m(VerifiedBadge, {
    user,
    className: 'AvocadoUserCard-verified'
  }) : null;
  const goProfile = e => {
    closeCard();
    navigate(e, profileHref);
  };
  return m("div", {
    className: `AvocadoUserCard-wrap${pos.above ? ' AvocadoUserCard-wrap--above' : ''}`,
    style: {
      top: `${pos.top}px`,
      left: `${pos.left}px`,
      '--avocado-usercard-arrow': `${Math.round(pos.arrow)}px`
    }
  }, m("div", {
    className: "AvocadoUserCard",
    oncreate: v => cardEl = v.dom,
    onmouseenter: () => cancelClose(),
    onmouseleave: () => scheduleClose()
  }, m("div", {
    className: "AvocadoUserCard-banner",
    style: cover ? {
      backgroundImage: `url(${cover})`,
      backgroundPosition: coverPosition(user)
    } : {}
  }), m("div", {
    className: "AvocadoUserCard-body"
  }, m("div", {
    className: "AvocadoUserCard-head"
  }, m("a", {
    className: `AvocadoUserCard-avatarLink${online ? ' AvocadoUserCard-avatarLink--online' : ''}`,
    href: profileHref,
    "aria-label": displayName(user),
    onclick: goProfile
  }, m(Avatar, {
    user: user,
    className: "AvocadoUserCard-avatar"
  })), m("div", {
    className: "AvocadoUserCard-names"
  }, m("span", {
    className: "AvocadoUserCard-nameRow"
  }, m("a", {
    className: "AvocadoUserCard-displayName",
    href: profileHref,
    onclick: goProfile
  }, displayName(user)), verified, m(CakedayBadge, {
    user: user
  }), badges.length > 0 && m("ul", {
    className: "badges AvocadoUserCard-badges"
  }, listItems(badges.slice(0, 3)))), m("span", {
    className: "AvocadoUserCard-username"
  }, "@", user.username?.(), !online && lastSeen && m("span", {
    className: "AvocadoUserCard-presence"
  }, trans('ramon-avocado.forum.user_card.last_seen', 'Seen {time}', {
    time: formatTimeLabel(lastSeen)
  }))))), bioHtml ? m("div", {
    className: "AvocadoUserCard-bio AvocadoUserCard-bio--html"
  }, trustedHtml(bioHtml)) : bio ? m("p", {
    className: "AvocadoUserCard-bio"
  }, truncate(bio, 180)) : null, m("div", {
    className: "AvocadoUserCard-stats"
  }, m("span", {
    className: "AvocadoUserCard-stat"
  }, m("strong", null, discussions !== null ? discussions : '–'), trans('ramon-avocado.forum.user_card.discussions', 'discussions')), m("span", {
    className: "AvocadoUserCard-stat"
  }, m("strong", null, posts !== null ? posts : '–'), trans('ramon-avocado.forum.user_card.posts', 'posts')), joined && m("span", {
    className: "AvocadoUserCard-stat"
  }, m("strong", null, formatDate(new Date(joined), {
    month: 'short',
    year: 'numeric'
  })), trans('ramon-avocado.forum.user_card.joined_label', 'joined'))), m("div", {
    className: "AvocadoUserCard-actions"
  }, m("a", {
    className: "Button Button--primary AvocadoUserCard-profileBtn",
    href: profileHref,
    onclick: goProfile
  }, m("i", {
    className: "fas fa-user",
    "aria-hidden": "true"
  }), trans('ramon-avocado.forum.user_card.view_profile', 'View profile')), canMessage && m("button", {
    className: "Button AvocadoUserCard-messageBtn",
    type: "button",
    onclick: () => openMessageComposer(user)
  }, m("i", {
    className: "fas fa-envelope",
    "aria-hidden": "true"
  }), trans('ramon-avocado.forum.user_card.message', 'Message'))))));
}
function openMessageComposer(user) {
  closeCard();

  // NÃO carregamos o MessageComposer pelo registry cross-bundle: nesta stack,
  // reg.asyncModuleImport('ext:flarum/messages/forum/components/MessageComposer')
  // resolve para o ComposerBody (abstrato, sem onsubmit) e o compositor nunca
  // abre / quebra no render. Em vez disso, reaproveitamos a ação nativa "Enviar
  // mensagem" que o flarum/messages registra em UserControls — o onclick dela usa
  // o import INTERNO do próprio bundle do messages e resolve a classe correta.
  try {
    const UserControls = flarum.reg?.get?.('core', 'forum/utils/UserControls');
    const items = UserControls?.userControls?.(user);
    const onclick = items?.get?.('sendMessage')?.attrs?.onclick;

    // Ausente quando flarum/messages não está instalado ou o usuário não pode
    // receber mensagens — nesses casos o botão do card simplesmente não aparece.
    if (typeof onclick === 'function') {
      onclick(new MouseEvent('click'));
    }
  } catch (e) {
    console.warn('[avocado] não foi possível abrir o compositor de mensagens', e);
  }
}

/**
 * Handlers de hover para acoplar o card a um elemento que JÁ existe, sem inserir
 * um wrapper. Use quando envolver o alvo mudaria a árvore que outras peças
 * esperam — ex.: o <h3 className="PostUser-name">, que precisa continuar sendo
 * filho direto de .PostUser (e ter o <a> como filho direto) para o CSS do tema e
 * para extensões que localizam o nó por vnode.children.find(match('PostUser-name')).
 * Retorna {} quando o card está desativado, então Object.assign vira no-op.
 */
export function hoverCardAttrs(user) {
  if (!user || !userCardEnabled()) return {};
  const open = e => {
    e.redraw = false;
    scheduleOpen(user, e.currentTarget);
  };
  return {
    onmouseenter: open,
    onmousemove: open,
    onmouseleave: e => {
      e.redraw = false;
      scheduleClose();
    }
  };
}
/**
 * Trigger do card Discourse-style. Envolva qualquer avatar/nome:
 *
 *   <UserHoverCard user={user}><Avatar user={user} /></UserHoverCard>
 *
 * O componente é só o gatilho — todo estado/render do card vive no singleton
 * acima. O mousemove rearma a abertura para sobreviver à recriação de
 * instância do PostStream. Hover-only por design: no touch o elemento
 * embrulhado mantém o tap normal (navegar ao perfil).
 */
export default class UserHoverCard extends Component {
  view(vnode) {
    const user = this.attrs.user;
    if (!user || !userCardEnabled()) return vnode.children;
    return m("span", Object.assign({
      className: "AvocadoUserCard-trigger"
    }, hoverCardAttrs(user)), vnode.children);
  }
}
flarum.reg.add('ramon-avocado', 'forum/components/shared/UserHoverCard', UserHoverCard);